import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import AlertToast from '../../../components/common/AlertToast';
import PolicyTab from '../../../components/common/PolicyTab';
import ClassContextPicker, { useSchoolConfig } from '../../../components/common/ClassContextPicker';

// ─── Helpers ────────────────────────────────────────────────────────────────
const newId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const blankChapter = () => ({ _id: newId('ch'), name: '', order: 0, hours: 0, topics: [] });
const blankTopic   = () => ({ _id: newId('t'),  title: '', order: 0, hours: 0, isComplete: false, completedAt: null, completedBy: null, notes: '' });

// ─── Main ───────────────────────────────────────────────────────────────────
export default function SyllabusTrackingPage() {
  const { user } = useAuth();
  const role = user?.roleKey || '';
  const canEdit = ['admin','manager','team_lead','faculty','teacher'].includes(role) || user?.userType === 'super_admin';

  const cfg = useSchoolConfig();

  // Default to the picker view — that's the main use case (pick class+section+
  // subject → see syllabus → tick today's completed topics). Overview is
  // secondary, used by HODs/managers reviewing the school as a whole.
  const [tab, setTab]                       = useState('detail');   // 'detail' | 'overview' | 'policy'
  const [filter, setFilter]                 = useState({});
  const [overview, setOverview]             = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [pickedContext, setPickedContext]   = useState({ classGrade: '', section: '', subject: '' });
  const [syllabus, setSyllabus]             = useState(null); // current detail record
  const [chapters, setChapters]             = useState([]);
  const [academicYear, setAcademicYear]     = useState('');
  const [detailLoading, setDetailLoading]   = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [toast, setToast]                   = useState(null);

  // Import-from-Course-Content state
  const [importing, setImporting]           = useState(false);
  const [importPicker, setImportPicker]     = useState(null);   // { courses: [...] } when multiple match

  // ─── Overview load ─────────────────────────────────────────────────────
  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const params = {};
      if (filter.classGrade) params.classGrade = filter.classGrade;
      if (filter.section)    params.section    = filter.section;
      if (filter.subject)    params.subject    = filter.subject;
      const r = await api.get('/education/syllabus/list', { params });
      setOverview(r.data?.data?.syllabi || r.data?.syllabi || []);
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load syllabi.' });
    }
    setOverviewLoading(false);
  }, [filter]);

  useEffect(() => { if (tab === 'overview') fetchOverview(); }, [tab, fetchOverview]);

  // ─── Detail load ───────────────────────────────────────────────────────
  const fetchDetail = useCallback(async (ctx) => {
    if (!ctx.classGrade || !ctx.section || !ctx.subject) {
      setSyllabus(null); setChapters([]); setAcademicYear(''); return;
    }
    setDetailLoading(true);
    try {
      const r = await api.get('/education/syllabus', {
        params: { class_grade: ctx.classGrade, section: ctx.section, subject: ctx.subject },
      });
      const rec = r.data?.data || r.data;
      if (rec) {
        setSyllabus(rec);
        setChapters(Array.isArray(rec.recordData?.chapters) ? rec.recordData.chapters : []);
        setAcademicYear(rec.recordData?.academic_year || '');
      } else {
        setSyllabus(null); setChapters([]); setAcademicYear('');
      }
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load syllabus.' });
    }
    setDetailLoading(false);
  }, []);

  // Open detail tab from overview row
  const openDetailFor = (row) => {
    const ctx = { classGrade: row.classGrade, section: row.section, subject: row.subject };
    setPickedContext(ctx);
    setTab('detail');
    fetchDetail(ctx);
  };

  // Auto-fetch when context changes on detail tab
  useEffect(() => {
    if (tab === 'detail') fetchDetail(pickedContext);
  }, [tab, pickedContext, fetchDetail]);

  // ─── Editing helpers ───────────────────────────────────────────────────
  const addChapter = () => setChapters(prev => [...prev, { ...blankChapter(), order: prev.length + 1 }]);
  const removeChapter = (chId) => setChapters(prev => prev.filter(ch => ch._id !== chId));
  const updateChapter = (chId, patch) => setChapters(prev => prev.map(ch => ch._id === chId ? { ...ch, ...patch } : ch));
  const addTopic = (chId) => setChapters(prev => prev.map(ch => ch._id !== chId ? ch : ({ ...ch, topics: [...ch.topics, { ...blankTopic(), order: (ch.topics || []).length + 1 }] })));
  const removeTopic = (chId, tId) => setChapters(prev => prev.map(ch => ch._id !== chId ? ch : ({ ...ch, topics: ch.topics.filter(t => t._id !== tId) })));
  const updateTopic = (chId, tId, patch) => setChapters(prev => prev.map(ch => ch._id !== chId ? ch : ({
    ...ch, topics: ch.topics.map(t => t._id === tId ? { ...t, ...patch } : t),
  })));

  const totalTopics = chapters.reduce((s, ch) => s + (ch.topics?.length || 0), 0);
  const doneTopics  = chapters.reduce((s, ch) => s + (ch.topics || []).filter(t => t.isComplete).length, 0);
  const percent     = totalTopics ? Math.round((doneTopics / totalTopics) * 100) : 0;

  // ─── Import from Course Content ───────────────────────────────────────
  // Pulls modules/topics from a matching Course Content course (same class +
  // subject) and pre-fills the syllabus tree. Staff then ticks completed topics
  // day-by-day as they teach. Only titles + structure are imported — actual
  // course content blocks (text/images/AI/etc.) stay in Course Content.
  const importFromCourseContent = async () => {
    if (!pickedContext.classGrade || !pickedContext.subject) {
      return setToast({ type: 'danger', msg: 'Pick a class and subject first.' });
    }
    setImporting(true);
    try {
      // Try the targeted fuzzy lookup first (matches "Class 9 (Science)" etc.).
      const tightR = await api.get('/education/courses', {
        params: { classGrade: pickedContext.classGrade, subject: pickedContext.subject, fuzzy: true, limit: 50 },
      });
      const tight = tightR.data?.records || [];

      if (tight.length === 1) {
        await applyImportFromCourse(tight[0]._id);
        return;
      }
      if (tight.length > 1) {
        setImportPicker({ courses: tight, mode: 'matched' });
        return;
      }

      // Zero exact-fuzzy matches — fall back to listing EVERY course so the
      // user can see what's actually in the DB and pick visually. Often the
      // class/subject labels diverged between Course Content and the syllabus
      // picker, and listing them all is the fastest way to surface that.
      const allR = await api.get('/education/courses', { params: { limit: 200 } });
      const all = allR.data?.records || [];

      // Surface diagnostic counts so the user can see what was tried.
      let diag = '';
      try {
        const [cByClass, cBySubject] = await Promise.all([
          api.get('/education/courses', { params: { classGrade: pickedContext.classGrade, fuzzy: true, limit: 50 } }),
          api.get('/education/courses', { params: { subject: pickedContext.subject, fuzzy: true, limit: 50 } }),
        ]);
        const byCls = cByClass.data?.records || [];
        const bySub = cBySubject.data?.records || [];
        diag = `Class-only matches: ${byCls.length} · Subject-only matches: ${bySub.length} · Total courses in tenant: ${all.length}`;
      } catch (_) { diag = `Total courses in tenant: ${all.length}`; }

      if (all.length === 0) {
        setToast({
          type: 'warning',
          msg: 'No courses exist anywhere in this tenant. Create a course in Course Content / LMS first, then come back to import.',
        });
      } else {
        // Show every course in the tenant — the user can pick the right one
        // even if the class/subject labels don't line up with the picker.
        setImportPicker({ courses: all, mode: 'all', diag });
      }
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load courses.' });
    }
    setImporting(false);
  };

  const applyImportFromCourse = async (courseId) => {
    setImporting(true);
    try {
      const r = await api.get(`/education/courses/${courseId}`);
      const full = r.data?.data || r.data;
      const importedChapters = (full.modules || []).map((m, mi) => ({
        _id:    newId('ch'),
        name:   m.title || `Module ${mi + 1}`,
        order:  mi + 1,
        hours:  0,
        topics: (m.topics || []).map((t, ti) => ({
          _id:           newId('t'),
          title:         t.title || `Topic ${ti + 1}`,
          order:         ti + 1,
          hours:         t.estimatedHours || 0,
          isComplete:    false,
          completedAt:   null,
          completedBy:   null,
          notes:         '',
        })),
      }));

      if (importedChapters.length === 0) {
        setToast({ type: 'warning', msg: `"${full.title}" has no modules yet. Add some in Course Content first.` });
      } else if (chapters.length > 0) {
        // Ask before wiping anything they may have already set up by hand.
        const ok = window.confirm(
          `This syllabus already has ${chapters.length} chapter(s). ` +
          `Replace with ${importedChapters.length} chapter(s) from "${full.title}"?\n\n` +
          `(Click Cancel to merge instead — the imported content will be appended.)`
        );
        if (ok) {
          setChapters(importedChapters);
          setToast({ type: 'success', msg: `Replaced syllabus with ${importedChapters.length} chapters from "${full.title}". Click Save Syllabus to persist.` });
        } else {
          // Append (merge)
          const merged = [...chapters, ...importedChapters.map((ch, i) => ({ ...ch, order: chapters.length + i + 1 }))];
          setChapters(merged);
          setToast({ type: 'success', msg: `Appended ${importedChapters.length} chapters from "${full.title}". Click Save Syllabus to persist.` });
        }
      } else {
        setChapters(importedChapters);
        setToast({ type: 'success', msg: `Imported ${importedChapters.length} chapters (${importedChapters.reduce((s, ch) => s + ch.topics.length, 0)} topics) from "${full.title}". Tick what's been taught and click Save Syllabus.` });
      }
      setImportPicker(null);
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Import failed.' });
    }
    setImporting(false);
  };

  // ─── Save ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!pickedContext.classGrade || !pickedContext.section || !pickedContext.subject) {
      return setToast({ type: 'danger', msg: 'Pick a class, section, and subject before saving.' });
    }
    setSaving(true);
    try {
      const r = await api.put('/education/syllabus', {
        class_grade:   pickedContext.classGrade,
        section:       pickedContext.section,
        subject:       pickedContext.subject,
        academic_year: academicYear,
        chapters,
      });
      const rec = r.data?.data || r.data;
      setSyllabus(rec);
      setChapters(Array.isArray(rec.recordData?.chapters) ? rec.recordData.chapters : []);
      setToast({ type: 'success', msg: 'Syllabus saved.' });
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Save failed.' });
    }
    setSaving(false);
  };

  // ─── Toggle a topic complete (PATCH the single topic) ──────────────────
  const toggleTopic = async (chId, tId, isComplete) => {
    if (!syllabus?._id) {
      // Not saved yet — do an optimistic local update only.
      updateTopic(chId, tId, { isComplete, completedAt: isComplete ? new Date().toISOString() : null });
      return;
    }
    // Optimistic
    updateTopic(chId, tId, { isComplete, completedAt: isComplete ? new Date().toISOString() : null });
    try {
      await api.patch(`/education/syllabus/${syllabus._id}/topic`, { chapterId: chId, topicId: tId, isComplete });
    } catch (e) {
      // Roll back
      updateTopic(chId, tId, { isComplete: !isComplete });
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to update topic.' });
    }
  };

  return (
    <div>
      <PageHeader title="Syllabus Tracking" icon="bi-journal-bookmark"
        subtitle="Track topic-by-topic progress for every class and subject"
        actions={(
          <span className="badge bg-primary bg-opacity-10 text-primary">
            <i className="bi bi-info-circle me-1"/>Configure classes/subjects in School Configuration
          </span>
        )}/>
      {toast && <AlertToast type={toast.type} message={toast.msg} show={true} onClose={() => setToast(null)} />}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${tab === 'detail' ? 'active' : ''}`} onClick={() => setTab('detail')}>
          <i className="bi bi-card-checklist me-1"/>Pick Class & Update Progress
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
          <i className="bi bi-grid-3x3 me-1"/>School-wide Overview
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'policy' ? 'active' : ''}`} onClick={() => setTab('policy')}>
          <i className="bi bi-journal-text me-1"/>Policy & Manual
        </button></li>
      </ul>

      {/* ───────────────────────────────────────────────── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <ClassContextPicker value={filter} onChange={setFilter} compact />
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span className="fw-semibold"><i className="bi bi-collection me-2 text-primary"/>Configured Syllabi</span>
              <button className="btn btn-outline-secondary btn-sm" onClick={fetchOverview}><i className="bi bi-arrow-clockwise"/></button>
            </div>
            <div className="card-body p-0">
              {overviewLoading ? (
                <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"/></div>
              ) : overview.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-inbox fs-1 d-block mb-2"/>
                  No syllabi configured yet. Switch to <strong>Detail / Edit</strong> tab to create one.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Class / Grade</th>
                        <th>Section</th>
                        <th>Subject</th>
                        <th>Academic Year</th>
                        <th style={{ width: 80 }}>Chapters</th>
                        <th style={{ width: 80 }}>Topics</th>
                        <th style={{ width: 220 }}>Progress</th>
                        <th style={{ width: 110 }}>Updated</th>
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.map(s => (
                        <tr key={s._id} role="button" onClick={() => openDetailFor(s)}>
                          <td className="fw-semibold">{s.classGrade || '—'}</td>
                          <td>{s.section || '—'}</td>
                          <td>{s.subject || '—'}</td>
                          <td>{s.academicYear || '—'}</td>
                          <td>{s.chapterCount || 0}</td>
                          <td><span className="text-success fw-semibold">{s.completedTopics}</span><span className="text-muted">/{s.totalTopics}</span></td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress flex-grow-1" style={{ height: 8 }}>
                                <div className={`progress-bar bg-${s.percent >= 75 ? 'success' : s.percent >= 40 ? 'warning' : 'danger'}`}
                                  style={{ width: `${s.percent}%` }}/>
                              </div>
                              <span className="small fw-semibold" style={{ minWidth: 38, textAlign: 'right' }}>{s.percent}%</span>
                            </div>
                          </td>
                          <td className="small text-muted">{new Date(s.updatedAt).toLocaleDateString('en-IN')}</td>
                          <td><i className="bi bi-pencil-square text-primary"/></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ───────────────────────────────────────────────── DETAIL TAB ──── */}
      {tab === 'detail' && (
        <>
          {/* Prominent picker — first and most important thing on this view */}
          <div className="card mb-3 border-primary border-2">
            <div className="card-header bg-primary bg-opacity-10 py-2">
              <span className="fw-semibold text-primary">
                <i className="bi bi-funnel-fill me-2"/>
                Step 1 — Pick the class, section and subject to load its syllabus
              </span>
            </div>
            <div className="card-body">
              <ClassContextPicker
                value={pickedContext}
                onChange={setPickedContext}
                required={['classGrade','section','subject']}
              />
              <div className="row g-2 mt-2">
                <div className="col-md-3">
                  <label className="form-label small mb-1">Academic Year (optional)</label>
                  <input className="form-control form-control-sm" placeholder="e.g. 2026-27" value={academicYear} onChange={e => setAcademicYear(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {detailLoading ? (
            <div className="text-center py-4"><LoadingSpinner /></div>
          ) : !pickedContext.classGrade || !pickedContext.section || !pickedContext.subject ? (
            <div className="card">
              <div className="card-body text-center py-5 text-muted">
                <i className="bi bi-arrow-up-circle fs-1 d-block mb-2"/>
                <strong>Pick the class, section and subject above to load its syllabus.</strong>
                <div className="small mt-2">
                  Once selected, you'll see every chapter and topic. Faculty can tick what's been taught — the
                  class progress percentage updates instantly so students and parents can see how much of the
                  syllabus has been covered.
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Class progress banner — visible to everyone (faculty / students / parents) */}
              <div className="card mb-3" style={{ background: 'linear-gradient(135deg,#e0f2fe,#dcfce7)', border: 'none' }}>
                <div className="card-body py-3">
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                    <div>
                      <h5 className="mb-0 fw-bold">
                        <i className="bi bi-graph-up-arrow me-2 text-success"/>
                        Class Progress · {pickedContext.classGrade} {pickedContext.section} · {pickedContext.subject}
                      </h5>
                      {syllabus && <div className="small text-muted mt-1">Last updated {new Date(syllabus.updatedAt).toLocaleString('en-IN')}</div>}
                    </div>
                    <span className="badge bg-success-subtle text-success fs-6">
                      {percent}% covered
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="progress flex-grow-1" style={{ height: 18 }}>
                      <div className={`progress-bar bg-${percent >= 75 ? 'success' : percent >= 40 ? 'warning' : 'danger'} fw-bold`}
                        style={{ width: `${percent}%`, fontSize: 13 }}>
                        {percent > 8 ? `${percent}%` : ''}
                      </div>
                    </div>
                    <span className="fw-bold" style={{ minWidth: 110 }}>
                      <span className="text-success">{doneTopics}</span>
                      <span className="text-muted"> / {totalTopics}</span>
                      <span className="text-muted small d-block" style={{ lineHeight: 1 }}>topics covered</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Recently completed (last 7 days) — gives parents/students a "what was taught this week" view */}
              <RecentCompletions chapters={chapters} />


              {/* Chapters tree */}
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">
                    <i className="bi bi-list-nested me-2 text-primary"/>
                    {canEdit ? 'Step 2 — Chapters & Topics (tick what was taught)' : 'Chapters & Topics'}
                  </span>
                  {canEdit && (
                    <div className="d-flex gap-2 flex-wrap">
                      <button className="btn btn-outline-info btn-sm" onClick={importFromCourseContent} disabled={importing}
                        title="Pull chapters & topics from a Course Content course matching this class & subject">
                        {importing
                          ? <span className="spinner-border spinner-border-sm me-1"/>
                          : <i className="bi bi-cloud-download me-1"/>}
                        Import from Course Content
                      </button>
                      <button className="btn btn-outline-primary btn-sm" onClick={addChapter}>
                        <i className="bi bi-plus me-1"/>Add Chapter
                      </button>
                      <button className="btn btn-success btn-sm" onClick={handleSave} disabled={saving}>
                        {saving ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-save me-1"/>}
                        Save Syllabus
                      </button>
                    </div>
                  )}
                </div>
                <div className="card-body">
                  {chapters.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="bi bi-journal text-muted fs-2 d-block mb-2"/>
                      <h6 className="text-muted mb-3">No chapters yet for this class/subject.</h6>
                      {canEdit && (
                        <>
                          <p className="text-muted small mb-3" style={{ maxWidth: 480, margin: '0 auto' }}>
                            If you've already built the syllabus structure in <strong>Course Content</strong> (modules &amp; topics),
                            you can pull it here in one click. Otherwise add chapters manually.
                          </p>
                          <div className="d-flex gap-2 justify-content-center flex-wrap">
                            <button className="btn btn-info btn-sm" onClick={importFromCourseContent} disabled={importing}>
                              {importing ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-cloud-download me-1"/>}
                              Import from Course Content
                            </button>
                            <button className="btn btn-outline-primary btn-sm" onClick={addChapter}>
                              <i className="bi bi-plus me-1"/>Add Chapter Manually
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    chapters.map((ch, ci) => {
                      const chDone  = (ch.topics || []).filter(t => t.isComplete).length;
                      const chTotal = (ch.topics || []).length;
                      const chPct   = chTotal ? Math.round((chDone / chTotal) * 100) : 0;
                      return (
                        <div key={ch._id} className="border rounded p-3 mb-3" style={{ background: '#fafbfc' }}>
                          <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
                            <span className="badge bg-primary">Ch {ci + 1}</span>
                            {canEdit ? (
                              <input className="form-control form-control-sm flex-grow-1" placeholder="Chapter name"
                                value={ch.name} onChange={e => updateChapter(ch._id, { name: e.target.value })} />
                            ) : (
                              <span className="fw-semibold flex-grow-1">{ch.name || <em className="text-muted">Untitled chapter</em>}</span>
                            )}
                            <span className="badge bg-light text-dark border">
                              <span className="text-success">{chDone}</span>/{chTotal} topics · {chPct}%
                            </span>
                            {canEdit && (
                              <>
                                <button className="btn btn-outline-success btn-sm" onClick={() => addTopic(ch._id)}>
                                  <i className="bi bi-plus me-1"/>Topic
                                </button>
                                <button className="btn btn-outline-danger btn-sm" onClick={() => removeChapter(ch._id)} title="Remove chapter">
                                  <i className="bi bi-trash"/>
                                </button>
                              </>
                            )}
                          </div>
                          <div className="ms-3">
                            {(ch.topics || []).length === 0 ? (
                              <div className="text-muted small py-2">No topics yet. {canEdit && 'Click "Topic" to add one.'}</div>
                            ) : (
                              ch.topics.map((t, ti) => (
                                <div key={t._id} className="d-flex gap-2 align-items-center py-1">
                                  <input type="checkbox" className="form-check-input" disabled={!canEdit}
                                    checked={!!t.isComplete}
                                    onChange={e => toggleTopic(ch._id, t._id, e.target.checked)} />
                                  <span className="text-muted small" style={{ minWidth: 32 }}>{ti + 1}.</span>
                                  {canEdit ? (
                                    <input className={`form-control form-control-sm ${t.isComplete ? 'text-decoration-line-through text-muted' : ''}`}
                                      placeholder="Topic title"
                                      value={t.title}
                                      onChange={e => updateTopic(ch._id, t._id, { title: e.target.value })} />
                                  ) : (
                                    <span className={`flex-grow-1 ${t.isComplete ? 'text-decoration-line-through text-muted' : ''}`}>
                                      {t.title || <em className="text-muted">Untitled topic</em>}
                                    </span>
                                  )}
                                  <input type="number" min="0" step="0.5" className="form-control form-control-sm" style={{ width: 80 }}
                                    placeholder="hrs" disabled={!canEdit}
                                    value={t.hours || ''}
                                    onChange={e => updateTopic(ch._id, t._id, { hours: parseFloat(e.target.value) || 0 })}
                                    title="Estimated hours" />
                                  {t.isComplete && t.completedAt && (
                                    <span className="badge bg-success-subtle text-success small" title={new Date(t.completedAt).toLocaleString('en-IN')}>
                                      <i className="bi bi-check2 me-1"/>{new Date(t.completedAt).toLocaleDateString('en-IN')}
                                    </span>
                                  )}
                                  {canEdit && (
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => removeTopic(ch._id, t._id)} title="Remove topic">
                                      <i className="bi bi-x"/>
                                    </button>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'policy' && <PolicyTab moduleKey="syllabus_tracking" label="Syllabus Tracking — Manual & Policy" />}

      {/* Course picker — shown when multiple courses match, OR when zero
          exact matches exist (fallback "browse all" mode). */}
      {importPicker && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {importPicker.mode === 'all'
                    ? 'No exact match — pick any course to import from'
                    : 'Pick a course to import from'}
                </h5>
                <button className="btn-close" onClick={() => setImportPicker(null)}/>
              </div>
              <div className="modal-body">
                {importPicker.mode === 'all' ? (
                  <>
                    <div className="alert alert-warning small mb-3">
                      <i className="bi bi-exclamation-triangle me-1"/>
                      No course in Course Content matches <strong>{pickedContext.classGrade}</strong> · <strong>{pickedContext.subject}</strong> exactly.
                      Below is every course in your tenant — the labels probably differ slightly between Course Content
                      and your syllabus picker. Click the right one to import its chapters & topics.
                    </div>
                    {importPicker.diag && (
                      <div className="small text-muted mb-3"><i className="bi bi-info-circle me-1"/>{importPicker.diag}</div>
                    )}
                  </>
                ) : (
                  <p className="small text-muted mb-3">
                    Multiple courses match <strong>{pickedContext.classGrade} · {pickedContext.subject}</strong>. Pick the one whose chapters & topics should populate this syllabus.
                  </p>
                )}

                <div className="d-flex flex-column gap-2">
                  {importPicker.courses.map(c => {
                    // Highlight close matches in the "all courses" fallback so
                    // the right one stands out at a glance.
                    const classLabel = (c.classGrade || '').toLowerCase();
                    const subjLabel  = (c.subject    || '').toLowerCase();
                    const wantClass  = (pickedContext.classGrade || '').toLowerCase();
                    const wantSubj   = (pickedContext.subject    || '').toLowerCase();
                    const classMatch = wantClass && (classLabel.includes(wantClass) || wantClass.includes(classLabel));
                    const subjMatch  = wantSubj  && (subjLabel.includes(wantSubj)   || wantSubj.includes(subjLabel));
                    const both       = classMatch && subjMatch;
                    const partial    = !both && (classMatch || subjMatch);

                    return (
                      <button key={c._id}
                        className={`btn ${both ? 'btn-info' : partial ? 'btn-outline-info' : 'btn-outline-secondary'} text-start`}
                        onClick={() => applyImportFromCourse(c._id)} disabled={importing}>
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <div><strong>{c.title || '(untitled)'}</strong></div>
                            <div className="small">
                              <span className={classMatch ? 'fw-bold text-success' : ''}>
                                Class: {c.classGrade || '—'}
                              </span>
                              <span className="mx-2">·</span>
                              <span className={subjMatch ? 'fw-bold text-success' : ''}>
                                Subject: {c.subject || '—'}
                              </span>
                              {c.board        && <><span className="mx-2">·</span><span>Board: {c.board}</span></>}
                              {c.academicYear && <><span className="mx-2">·</span><span>Year: {c.academicYear}</span></>}
                            </div>
                          </div>
                          <span className="badge bg-light text-dark border flex-shrink-0">
                            {c.moduleCount || 0} mod · {c.topicCount || 0} topics
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setImportPicker(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── RecentCompletions ──────────────────────────────────────────────────────
// Read-only "what was taught this week" panel — visible to faculty / students /
// parents alike. Groups completed topics by completion date (latest first).
function RecentCompletions({ chapters }) {
  // Flatten chapters → topics, keep only completed ones with a date.
  const completed = [];
  (chapters || []).forEach(ch => {
    (ch.topics || []).forEach(t => {
      if (t.isComplete && t.completedAt) {
        completed.push({
          chapterName: ch.name || 'Chapter',
          title:       t.title || 'Topic',
          completedAt: new Date(t.completedAt),
          notes:       t.notes || '',
        });
      }
    });
  });

  if (completed.length === 0) return null;

  // Sort newest first.
  completed.sort((a, b) => b.completedAt - a.completedAt);

  // Show last 7 days. If nothing in the last 7 days, fall back to the latest 5.
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const inLastWeek   = completed.filter(c => c.completedAt >= sevenDaysAgo);
  const items        = inLastWeek.length > 0 ? inLastWeek : completed.slice(0, 5);
  const headerLabel  = inLastWeek.length > 0 ? 'Recently completed (last 7 days)' : 'Latest completed topics';

  // Group by yyyy-mm-dd so the user sees "what was taught Mon / Tue / ..."
  const byDay = {};
  items.forEach(c => {
    const k = c.completedAt.toISOString().slice(0, 10);
    if (!byDay[k]) byDay[k] = [];
    byDay[k].push(c);
  });
  const dayKeys = Object.keys(byDay).sort((a, b) => b.localeCompare(a));

  return (
    <div className="card mb-3">
      <div className="card-header py-2">
        <span className="fw-semibold"><i className="bi bi-calendar-check me-2 text-success"/>{headerLabel}</span>
        <span className="badge bg-success-subtle text-success ms-2">{items.length}</span>
      </div>
      <div className="card-body py-2">
        {dayKeys.map(k => {
          const day = new Date(k);
          const dayLabel = day.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' });
          const isToday = (new Date()).toDateString() === day.toDateString();
          return (
            <div key={k} className="mb-2">
              <div className="small fw-semibold text-muted mb-1" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>
                {isToday && <span className="badge bg-success me-2">TODAY</span>}{dayLabel}
              </div>
              <div className="d-flex flex-column gap-1 ms-3">
                {byDay[k].map((c, idx) => (
                  <div key={idx} className="d-flex align-items-center gap-2 small">
                    <i className="bi bi-check-circle-fill text-success"/>
                    <span><strong>{c.chapterName}</strong> · {c.title}</span>
                    {c.notes && <span className="text-muted">— {c.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
